import { useState, useEffect } from 'react';
import { getClassBackgroundColor, getClassBorderColor, getRumaalColor, getTextColorForBackground } from '../../utils/colors';
import { getDurationInSlots, formatTime, isHalfHourTime, getHalfHourOffset } from '../../utils/timeUtils';

const PublicClassBlock = ({ classItem, isSpanning = false, spanHeight = null, spanTop = 0 }) => {
  const backgroundColor = getClassBackgroundColor(classItem.type, classItem.ageGroup);
  const borderColor = getClassBorderColor(classItem.type, classItem.ageGroup);
  const rumaalColor = getRumaalColor(classItem.rumaalLevel || 'White');
  const textColorMode = getTextColorForBackground(backgroundColor);
  const isDarkBackground = textColorMode === 'light';
  const heightSlots = getDurationInSlots(classItem.duration);
  const [slotHeight, setSlotHeight] = useState(160); // Default to md+ size
  
  // Calculate responsive slot height based on screen size
  useEffect(() => {
    const updateSlotHeight = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setSlotHeight(128);
      } else if (width < 768) {
        setSlotHeight(144);
      } else {
        setSlotHeight(160);
      }
    };
    
    updateSlotHeight();
    window.addEventListener('resize', updateSlotHeight);
    return () => window.removeEventListener('resize', updateSlotHeight);
  }, []);
  
  const height = heightSlots * slotHeight;
  const halfHourOffset = isHalfHourTime(classItem.startTime) ? getHalfHourOffset(classItem.startTime, slotHeight) : 0;
  
  // Calculate capacity progress (visual only, no numbers)
  const studentCount = classItem.studentCount || 0;
  const capacity = 12; // Target capacity
  const capacityPercentage = Math.min((studentCount / capacity) * 100, 100);
  const isAtCapacity = studentCount >= capacity;
  const isOverCapacity = studentCount > capacity;
  
  // Determine progress bar color - visual indicators only
  const getProgressBarColor = () => {
    if (isAtCapacity || isOverCapacity) return '#EF4444'; // Red when at or over capacity
    if (capacityPercentage >= 75) return '#F59E0B'; // Orange when near full (75%+)
    if (capacityPercentage >= 50) return '#A7F3D0'; // Green when medium (50-75%)
    return '#D1D5DB'; // Grey when low (<50%)
  };

  // Only show the main block at the start time, spanning blocks are just continuation
  if (isSpanning) {
    const showProgressBar = classItem.duration > 60;
    
    return (
      <div
        className="absolute left-0 right-0"
        style={{
          top: `${spanTop}px`,
          height: spanHeight ? `${spanHeight}px` : '100%',
          backgroundColor,
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: borderColor !== 'transparent' ? `2px solid ${borderColor}` : 'none',
        }}
      >
        {/* Left accent stripe */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{
            backgroundColor: borderColor !== 'transparent' ? borderColor : rumaalColor,
            opacity: 0.6,
          }}
        />
        
        {/* Capacity progress bar - show on spanning block for classes > 60 minutes */}
        {showProgressBar && (
          <div className="absolute bottom-0 left-2.5 right-2.5 pb-1">
            <div className="text-center mb-2 px-1">
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md backdrop-blur-sm ${
                isDarkBackground 
                  ? 'bg-white/20 border border-white/30' 
                  : 'bg-gray-50/60'
              }`}>
                <svg className={`w-3 h-3 ${
                  isDarkBackground ? 'text-white/90' : 'text-gray-500'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-xs font-semibold tracking-wide ${
                  isDarkBackground ? 'text-white/90' : 'text-gray-700'
                }`}>
                  {formatTime(classItem.startTime)}
                </span>
              </div>
            </div>
            
            <div 
              className="h-1.5 rounded-full bg-white shadow-sm overflow-hidden"
              style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-200"
                style={{
                  width: `${Math.min(capacityPercentage, 100)}%`,
                  backgroundColor: getProgressBarColor(),
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="absolute left-0 right-0 rounded-md overflow-hidden"
      style={{
        top: `${halfHourOffset}px`,
        height: `${height}px`,
        backgroundColor,
        border: borderColor !== 'transparent' ? `2px solid ${borderColor}` : `1px solid rgba(0, 0, 0, 0.08)`,
        borderLeft: 'none',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Left accent stripe */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{
          backgroundColor: borderColor !== 'transparent' ? borderColor : rumaalColor,
          opacity: 0.6,
        }}
      />

      {/* Content */}
      <div className="h-full flex flex-col p-1.5 sm:p-2 md:p-2.5 relative">
        {/* Header with class name and Rumaal badge */}
        <div className="flex items-start justify-between mb-0.5 gap-1">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div 
              className={`text-xs sm:text-sm font-bold leading-tight ${
                isDarkBackground ? 'text-white' : 'text-gray-900'
              }`}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {classItem.name || `${classItem.type} – ${classItem.ageGroup}`}
            </div>
          </div>
          {/* Rumaal badge - color only */}
          <div className="flex-shrink-0">
            <div
              className="rounded-full w-4 h-1.5 sm:w-5 sm:h-2"
              style={{
                backgroundColor: rumaalColor,
                border: rumaalColor === '#FFFFFF' ? '1px solid #D1D5DB' : 'none',
              }}
              title={`Rumaal: ${classItem.rumaalLevel || 'White'}`}
            />
          </div>
        </div>

        {/* Instructor */}
        <div className={`text-xs sm:text-xs mb-0.5 flex items-center gap-1.5 sm:gap-1 ${
          isDarkBackground ? 'text-gray-200' : 'text-gray-600'
        }`}>
          {classItem.instructor && (
            <div className="flex items-center gap-1.5 sm:gap-1">
              <svg className={`w-4 h-4 sm:w-4 sm:h-4 flex-shrink-0 ${
                isDarkBackground ? 'text-gray-300' : 'text-gray-500'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className={`text-xs sm:text-xs ${
                isDarkBackground ? 'text-gray-100' : 'text-gray-800'
              }`}>{classItem.instructor}</span>
            </div>
          )}
        </div>

        {/* Capacity progress bar - only show for classes <= 60 minutes */}
        {classItem.duration <= 60 && (
          <div className="mt-auto">
            {/* Time above progress bar */}
            <div className="text-center mb-2 px-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50/60 backdrop-blur-sm">
                <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs font-semibold text-gray-700 tracking-wide">
                  {formatTime(classItem.startTime)}
                </span>
              </div>
            </div>
            
            {/* Visual capacity indicator - no numbers */}
            <div 
              className="h-1.5 rounded-full bg-white shadow-sm overflow-hidden"
              style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-200"
                style={{
                  width: `${Math.min(capacityPercentage, 100)}%`,
                  backgroundColor: getProgressBarColor(),
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicClassBlock;

